import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import * as Sentry from "@sentry/nextjs";
import { Button } from "design-system/components/button/button";
import { ButtonSize } from "design-system/components/button/button.types";
import {
  CameraContainer,
  CameraFrame,
  Corner,
  Countdown,
  CameraErrorContainer,
  CameraErrorText
} from "./practice-camera.styles";

export interface PracticeCameraProps {
  onImageCaptured: (image: string) => void;
}

export interface PracticeCameraRef {
  startCapture: () => void;
}

export const PracticeCamera = forwardRef<
  PracticeCameraRef,
  PracticeCameraProps
>(({ onImageCaptured }, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);
  const isCaptureInProgressRef = useRef(false);
  const cameraRequestIdRef = useRef(0);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const startCamera = useCallback(async () => {
    const requestId = ++cameraRequestIdRef.current;
    setCameraError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: {
            ideal: "environment"
          }
        },
        audio: false
      });

      if (requestId !== cameraRequestIdRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      if (requestId !== cameraRequestIdRef.current) return;

      Sentry.captureException(error, {
        tags: {
          component: "PracticeCamera",
          detail: "camera initialization error"
        }
      });
      setCameraError(
        "Kamera tidak dapat diakses. Periksa izin kamera kamu lalu coba lagi."
      );
    }
  }, []);

  useEffect(() => {
    const videoElement = videoRef.current;

    void startCamera();

    return () => {
      cameraRequestIdRef.current += 1;

      if (countdownIntervalRef.current !== null) {
        window.clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }

      isCaptureInProgressRef.current = false;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;

      if (videoElement) {
        videoElement.srcObject = null;
      }
    };
  }, [startCamera]);

  const captureImage = () => {
    const video = videoRef.current;

    if (!video || video.videoWidth === 0 || video.videoHeight === 0) return;

    // The preview uses `object-fit: cover`, so only the centered portion of
    // the camera frame is visible. Capture that same portion instead of the full sensor frame.
    const previewWidth = video.clientWidth;
    const previewHeight = video.clientHeight;

    if (previewWidth === 0 || previewHeight === 0) return;

    const sourceWidth = video.videoWidth;
    const sourceHeight = video.videoHeight;
    const previewAspectRatio = previewWidth / previewHeight;
    const sourceAspectRatio = sourceWidth / sourceHeight;

    let cropWidth = sourceWidth;
    let cropHeight = sourceHeight;
    let cropX = 0;
    let cropY = 0;

    if (sourceAspectRatio > previewAspectRatio) {
      cropWidth = sourceHeight * previewAspectRatio;
      cropX = (sourceWidth - cropWidth) / 2;
    } else if (sourceAspectRatio < previewAspectRatio) {
      cropHeight = sourceWidth / previewAspectRatio;
      cropY = (sourceHeight - cropHeight) / 2;
    }

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(cropWidth);
    canvas.height = Math.round(cropHeight);

    const context = canvas.getContext("2d");

    if (!context) return;

    context.drawImage(
      video,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );

    const image = canvas.toDataURL("image/jpeg", 0.9);

    onImageCaptured(image);
  };

  const startCapture = () => {
    const video = videoRef.current;

    if (
      isCaptureInProgressRef.current ||
      !video ||
      video.videoWidth === 0 ||
      video.videoHeight === 0
    ) {
      return;
    }

    isCaptureInProgressRef.current = true;

    setCountdown(3);

    let currentCount = 3;

    countdownIntervalRef.current = window.setInterval(() => {
      currentCount -= 1;

      if (currentCount === 0) {
        if (countdownIntervalRef.current !== null) {
          window.clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }

        setCountdown(null);
        isCaptureInProgressRef.current = false;
        captureImage();
        return;
      }

      setCountdown(currentCount);
    }, 1000);
  };

  useImperativeHandle(ref, () => ({
    startCapture
  }));

  return (
    <CameraContainer>
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover"
        }}
      />

      {!cameraError && (
        <CameraFrame>
          <Corner $position="topLeft" />
          <Corner $position="topRight" />
          <Corner $position="bottomLeft" />
          <Corner $position="bottomRight" />

          {countdown && <Countdown>{countdown}</Countdown>}
        </CameraFrame>
      )}

      {cameraError && (
        <CameraErrorContainer>
          <CameraErrorText>{cameraError}</CameraErrorText>
          <Button
            appearance="general"
            variant="primary"
            size={ButtonSize.md}
            onClick={() => void startCamera()}
          >
            Coba lagi
          </Button>
        </CameraErrorContainer>
      )}
    </CameraContainer>
  );
});

PracticeCamera.displayName = "PracticeCamera";
